import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {Observable} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {MatAutocomplete, MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {MatChipInputEvent} from '@angular/material/chips';
import {AuthConfig, OAuthService} from 'angular-oauth2-oidc';
import {authCodeFlowConfig} from '../../sso.config';
import {JwksValidationHandler} from 'angular-oauth2-oidc-jwks';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {DialogBodyComponent} from '../../components/dialog-body/dialog-body.component';
import * as moment from 'jalali-moment';
import {environment} from '@env/environment';

@Component({
  selector: 'app-code',
  templateUrl: './code.component.html',
  styleUrls: ['./code.component.css']
})
export class CodeComponent implements OnInit {

  hide = true;
  id_token: string = null;
  access_token: string = null;
  expire_in: any = null;
  expire_in_persian: any = null;
  token_type: string = null;
  refresh_token: string = null;
  getGrantedScopes: any = null;

  config: AuthConfig;

  codeFlowForm: FormGroup;

  clientIdOptions: string[] = [];
  filteredOptions: Observable<string[]>;

  separatorKeysCodes: number[] = [ENTER, COMMA];
  filteredScopes: Observable<string[]>;
  scopes: string[] = [];
  allScopes: string[] = ['openid', 'profile', 'phone', 'display_name', 'offline_access', 'email'];

  issuerList: string[] = ['https://accounts.idall.pro', 'https://dev.accounts.idall.pro'];

  @ViewChild('scopeInput') scopeInput: ElementRef<HTMLInputElement>;
  @ViewChild('scopeAuto') matAutocomplete: MatAutocomplete;
  clientSecretInputType = 'password';
  redirectUri: string;
  version: string;


  constructor(private oAuthService: OAuthService, public matDialog: MatDialog) {
    if (JSON.parse(window.localStorage.getItem('code-config'))) {
      this.oAuthService.configure(JSON.parse(window.localStorage.getItem('code-config')));
    } else {
      this.oAuthService.configure(authCodeFlowConfig);
    }
    this.oAuthService.tokenValidationHandler = new JwksValidationHandler();
    this.oAuthService.loadDiscoveryDocumentAndTryLogin().then((data) => {
      this.id_token = this.oAuthService.getIdToken();
      this.access_token = this.oAuthService.getAccessToken();
      this.expire_in = this.oAuthService.getAccessTokenExpiration() ? moment(new Date(this.oAuthService.getAccessTokenExpiration()), 'YYYY/MM/DD HH:mm:ss').locale('en').format('YYYY/MM/DD HH:mm:ss') : null;
      this.refresh_token = this.oAuthService.getRefreshToken();
      this.getGrantedScopes = this.oAuthService.getGrantedScopes();
    });

  }

  ngOnInit(): void {

    if (environment.localhash) {
      this.issuerList.push('https://localhash.accounts.idall.pro');
    }

    this.initForm();

    this.redirectUri = window.location.origin + '/code';
    this.version = environment.version;

    if (JSON.parse(window.localStorage.getItem('codeFlowClientIds'))) {

      this.clientIdOptions = JSON.parse(window.localStorage.getItem('codeFlowClientIds'));
    }

    if (JSON.parse(window.localStorage.getItem('codeFlowScopes'))) {

      this.allScopes = JSON.parse(window.localStorage.getItem('codeFlowScopes'));
    }

    if (JSON.parse(window.localStorage.getItem('code-config'))) {

      const config = JSON.parse(window.localStorage.getItem('code-config'));

      this.codeFlowForm.controls.clientId.setValue(config.clientId);
      this.codeFlowForm.controls.dummyClientSecret.setValue(config.dummyClientSecret);
      this.scopes = config.scope.split(' ');
      this.codeFlowForm.controls.disablePKCE.setValue(!config.disablePKCE);
      this.codeFlowForm.controls.promptLogin.setValue(config.promptLogin);
    }

    this.filteredOptions = this.codeFlowForm.get('clientId').valueChanges
      .pipe(
        startWith(''),
        map(value => this.clientIdFilter(value))
      );

    this.filteredScopes = this.codeFlowForm.get('scope').valueChanges.pipe(
      startWith(null),
      map((scope: string | null) => {
        return scope ? this.scopeFilter(scope) : this.allScopes.slice();
      }));
  }

  initForm() {

    this.codeFlowForm = new FormGroup({

      issuer: new FormControl(null, Validators.required),
      clientId: new FormControl(null, Validators.required),
      dummyClientSecret: new FormControl(null),
      scope: new FormControl(null),
      disablePKCE: new FormControl(false),
      promptLogin: new FormControl(false),
      iframe: new FormControl(false)
    });
  }

  openDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = this.config;
    dialogConfig.width = '800px';
    dialogConfig.height = '300px';

    const dialog = this.matDialog.open(DialogBodyComponent, dialogConfig);

    dialog.afterClosed().subscribe((res) => {
      console.log(res);
    });
  }

  clientIdFilter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.clientIdOptions.filter(option => option.toLowerCase().includes(filterValue));
  }

  add(event: MatChipInputEvent): void {

    const input = event.input;
    const value = event.value;

    // Add our scope
    if ((value || '').trim()) {

      if (this.scopes.indexOf(value.trim()) === -1) {

        this.scopes.push(value.trim());
      }

      if (window.localStorage.getItem('codeFlowScopes')) {

        const scopes = JSON.parse(window.localStorage.getItem('codeFlowScopes'));

        if (this.scopes.indexOf(value.trim()) === -1) {

          window.localStorage.setItem('codeFlowScopes', JSON.stringify([...scopes, value.trim()]));

          this.allScopes.push(value.trim());
        }

      } else {

        window.localStorage.setItem('codeFlowScopes', JSON.stringify([...this.allScopes, value.trim()]));

        this.allScopes.push(value.trim());
      }
    }


    // Reset the input value
    if (input) {
      input.value = '';
    }

    this.codeFlowForm.controls.scope.setValue(null);
  }

  remove(scope: string): void {
    const index = this.scopes.indexOf(scope);

    if (index >= 0) {
      this.scopes.splice(index, 1);
    }
  }

  selected(event: MatAutocompleteSelectedEvent): void {

    if (this.scopes.indexOf(event.option.viewValue.trim()) === -1) {

      this.scopes.push(event.option.viewValue.trim());
    }

    this.scopeInput.nativeElement.value = '';
    this.codeFlowForm.controls.scope.setValue(null);
  }

  scopeFilter(value: string): string[] {
    const filterValue = value.toLowerCase();

    return this.allScopes.filter(scope => scope.toLowerCase().indexOf(filterValue) === 0);
  }

  submitCodeFlow() {

    if (this.codeFlowForm.valid) {

      this.config = {
        issuer: this.codeFlowForm.get('issuer').value,
        redirectUri: window.location.origin + '/code',
        clientId: this.codeFlowForm.get('clientId').value,
        dummyClientSecret: this.codeFlowForm.get('dummyClientSecret').value,
        responseType: 'code',
        scope: this.scopes.join(' '),
        // showDebugInformation: true,
        disablePKCE: !this.codeFlowForm.get('disablePKCE').value
      };

      if (this.codeFlowForm.get('promptLogin').valid) {
        this.config.customQueryParams = {
          'prompt': 'login'
        };
      }

      this.oAuthService.configure(this.config);
      window.localStorage.setItem('code-config', JSON.stringify(this.config));
      // this.oAuthService.setupAutomaticSilentRefresh();
      this.oAuthService.tokenValidationHandler = new JwksValidationHandler();
      sessionStorage.removeItem('refresh_token');
      this.oAuthService.loadDiscoveryDocumentAndTryLogin().then((data) => {
        // console.log('data', data);
      });

      if (this.codeFlowForm.get('iframe').value) {
        this.openDialog();
      } else {
        this.oAuthService.initCodeFlow();
      }
    }
  }

  changeClientSecretInputType($event) {

    $event.preventDefault();
    if (this.clientSecretInputType === 'password') {
      this.clientSecretInputType = 'text';
    } else if (this.clientSecretInputType === 'text') {
      this.clientSecretInputType = 'password';
    }
  }

  enterClientId($event: any) {

    $event.preventDefault();

    if ((this.codeFlowForm.get('clientId').value || '').trim()) {

      if (window.localStorage.getItem('codeFlowClientIds')) {

        const clientIds = JSON.parse(window.localStorage.getItem('codeFlowClientIds'));

        if (clientIds.indexOf(this.codeFlowForm.get('clientId').value.trim()) === -1) {

          window.localStorage.setItem('codeFlowClientIds', JSON.stringify([...clientIds, this.codeFlowForm.get('clientId').value.trim()]));

          this.clientIdOptions.push(this.codeFlowForm.get('clientId').value.trim());

        }

      } else {

        window.localStorage.setItem('codeFlowClientIds', JSON.stringify([
          ...this.clientIdOptions, this.codeFlowForm.get('clientId').value.trim()
        ]));

        this.clientIdOptions.push(this.codeFlowForm.get('clientId').value.trim());
      }

    }
  }

  deleteClientId($event: MouseEvent, option: string) {

    $event.stopPropagation();

    const index = this.clientIdOptions.findIndex(item => item === option);

    this.clientIdOptions = this.clientIdOptions.filter(item => item !== option);

    console.log(this.clientIdOptions);

    const clientIds = JSON.parse(window.localStorage.getItem('codeFlowClientIds'));

    clientIds.splice(index, 1);

    window.localStorage.setItem('codeFlowClientIds', JSON.stringify(clientIds));
  }

  hideIcon($event: MouseEvent) {
    $event.preventDefault();
    this.hide = !this.hide;
  }
}
